/**
 * Dev-only helpers for anchor injection vs API document links (`/documentlink/by-document/:id`).
 *
 * Enable logging in the browser console (same session as legislation HTML view):
 * `VITE_DOC_LINK_INJECTION_DEBUG=true` or `=1` in `.env.local`, then reload.
 *
 * Inspect structured data: `window.__LARA_DOC_LINK_INJECTION_DEBUG__` (summary, report, referencesSidebarSummary).
 */
import type { DocumentLink } from "@/models/document-link/DocumentLink";
import { RefType } from "@/models/document-link/DocumentLink";
import { parseLinkExtraMetadata } from "@/utils/documentLinkOtherSide";
import type {
  DocumentLinkInjectionDebugReport,
  DocumentLinkPerLinkOutcome,
  DocumentLinkSkipReason,
} from "./documentLinkInjectionPlan";
import { formatDocumentLinkInjectionReport } from "./documentLinkInjectionPlan";
import { isSameDocumentBothEnds } from "./documentLinkDivUtils";
import {
  formatReferencesSidebarVisibilityReport,
  summarizeReferencesSidebarVisibility,
  type ReferencesSidebarVisibilitySummary,
} from "@/utils/documentLinkReferencesSidebarVisibility";

/** Ref types produced by autogeneration on law texts that we care about for injection gaps. */
export const DOC_LINK_INJECTION_AUTOGEN_REF_TYPES: ReadonlySet<RefType> =
  new Set([
    RefType.INTERNAL,
    RefType.TEXT_LEVEL_TO_TEXT_LEVEL,
    RefType.TEXT_LEVEL_TO_DOCUMENT,
  ]);

function refTypeForLink(link: DocumentLink | undefined): RefType | undefined {
  if (!link) return undefined;
  const meta = parseLinkExtraMetadata(link.extraMetadata);
  const rt = meta.ref_type;
  if (rt === undefined || rt === null) return undefined;
  if (typeof rt !== "string") return undefined;
  return Object.values(RefType).includes(rt as RefType)
    ? (rt as RefType)
    : undefined;
}

function isAutogenRefType(ref: RefType | undefined): ref is RefType {
  return ref !== undefined && DOC_LINK_INJECTION_AUTOGEN_REF_TYPES.has(ref);
}

/**
 * True when the open document is the origin or destination end that has no text-level
 * anchor by API design (e.g. `text_level_to_document` with empty `destinationHtmlIds`).
 * {@link no_match_text} is expected — not an injection/matching bug.
 *
 * Same-document-both-ends links are handled first (`isSameDocumentBothEnds`); that shape
 * is always internal refs and uses the origin side only, matching `getEffectiveDivFields`.
 */
function isExpectedDocumentLevelNoInBodySkip(
  link: DocumentLink,
  documentId: string | undefined,
  o: DocumentLinkPerLinkOutcome
): boolean {
  if (
    o.status !== "skipped" ||
    o.skipReason !== "no_match_text" ||
    !documentId
  ) {
    return false;
  }

  if (isSameDocumentBothEnds(link, documentId)) {
    return !link.originHtmlIds?.length && !link.originUniqueMatchText?.trim();
  }

  if (link.originDocumentId === documentId) {
    return !link.originHtmlIds?.length && !link.originUniqueMatchText?.trim();
  }
  if (link.destinationDocumentId === documentId) {
    return (
      !link.destinationHtmlIds?.length &&
      !link.destinationUniqueMatchText?.trim()
    );
  }
  return false;
}

/**
 * `text_level_to_text_level`: the origin passage points at a destination block id; the
 * destination document does not carry reciprocal match/chunk text. When
 * `destinationUniqueMatchText` and `destinationUniqueChunkText` are both unset, no
 * in-body anchor is expected on the destination — only on the origin.
 *
 * Omit from actionable skips only when both destination strings are null/empty (if either
 * is set, injection may be intentional and should surface).
 */
function isExpectedTextLevelToTextLevelDestinationSkip(
  link: DocumentLink,
  documentId: string | undefined,
  o: DocumentLinkPerLinkOutcome
): boolean {
  if (
    o.status !== "skipped" ||
    o.skipReason !== "no_match_text" ||
    !documentId
  ) {
    return false;
  }
  if (refTypeForLink(link) !== RefType.TEXT_LEVEL_TO_TEXT_LEVEL) {
    return false;
  }
  if (link.destinationDocumentId !== documentId) {
    return false;
  }
  if (link.destinationUniqueMatchText?.trim()) {
    return false;
  }
  if (link.destinationUniqueChunkText?.trim()) {
    return false;
  }
  return true;
}

export interface AutogenSkippedInjectionEntry {
  linkId: string;
  refType: RefType;
  skipReason: DocumentLinkSkipReason;
  detail?: string;
  uniqueMatchText?: string;
  uniqueChunkText?: string;
  primaryHtmlId?: string;
}

export interface AutogenInjectionDebugSummary {
  documentId: string | undefined;
  totalLinksInPlan: number;
  /** Skipped anchor injection, ref_type in autogen set */
  skippedAutogen: AutogenSkippedInjectionEntry[];
  /** Injected successfully, ref_type in autogen set */
  injectedAutogenCount: number;
  /** Skipped for other reasons or ref_type outside autogen set (not listed in skippedAutogen) */
  skippedOtherCount: number;
  /**
   * Autogen `no_match_text` on the document end that has no text anchor (document-level side);
   * omitted from {@link skippedAutogen} as expected, not a gap to fix.
   */
  omittedExpectedDocumentLevelNoAnchor: number;
  /**
   * `text_level_to_text_level` on the destination doc with no destination match/chunk
   * (anchor is origin-only); omitted from {@link skippedAutogen} as expected.
   */
  omittedExpectedTextLevelToTextLevelDestination: number;
}

/**
 * Cross-reference injection outcomes with API links and {@link DOC_LINK_INJECTION_AUTOGEN_REF_TYPES}.
 */
export function summarizeAutogenDocumentLinkInjection(
  report: DocumentLinkInjectionDebugReport,
  documentLinksMap: Map<string, DocumentLink>,
  documentId: string | undefined
): AutogenInjectionDebugSummary {
  const skippedAutogen: AutogenSkippedInjectionEntry[] = [];
  let injectedAutogenCount = 0;
  let skippedOtherCount = 0;
  let omittedExpectedDocumentLevelNoAnchor = 0;
  let omittedExpectedTextLevelToTextLevelDestination = 0;

  for (const o of report.perLink) {
    const link = documentLinksMap.get(o.linkId);
    const rt = refTypeForLink(link);

    if (o.status === "injected") {
      if (isAutogenRefType(rt)) {
        injectedAutogenCount += 1;
      }
      continue;
    }

    if (isAutogenRefType(rt)) {
      if (
        link &&
        isExpectedTextLevelToTextLevelDestinationSkip(link, documentId, o)
      ) {
        omittedExpectedTextLevelToTextLevelDestination += 1;
        continue;
      }
      if (link && isExpectedDocumentLevelNoInBodySkip(link, documentId, o)) {
        omittedExpectedDocumentLevelNoAnchor += 1;
        continue;
      }
      skippedAutogen.push({
        linkId: o.linkId,
        refType: rt,
        skipReason: o.skipReason ?? "not_found_in_subtree",
        detail: o.detail,
        uniqueMatchText: o.api.uniqueMatchText,
        uniqueChunkText: o.api.uniqueChunkText,
        primaryHtmlId: o.api.primaryHtmlId,
      });
    } else {
      skippedOtherCount += 1;
    }
  }

  return {
    documentId,
    totalLinksInPlan: report.apiLinkIds.length,
    skippedAutogen,
    injectedAutogenCount,
    skippedOtherCount,
    omittedExpectedDocumentLevelNoAnchor,
    omittedExpectedTextLevelToTextLevelDestination,
  };
}

export function formatAutogenSkippedInjectionReport(
  summary: AutogenInjectionDebugSummary
): string {
  const lines: string[] = [];
  lines.push("=== Autogenerated document links — anchor injection gaps ===");
  lines.push(
    `documentId=${summary.documentId ?? "(none)"} | totalLinksLoaded=${summary.totalLinksInPlan}`
  );
  lines.push(
    `injectedAutogen(ref∈{internal,text_level_to_text_level,text_level_to_document})=${summary.injectedAutogenCount}`
  );
  lines.push(
    `skippedOtherRefOrMissingRef=${summary.skippedOtherCount} (not listed below)`
  );
  if (summary.omittedExpectedDocumentLevelNoAnchor > 0) {
    lines.push(
      `omittedExpectedDocumentLevelNoAnchor=${summary.omittedExpectedDocumentLevelNoAnchor} (no text anchor on this side of the link — not an injection gap)`
    );
  }
  if (summary.omittedExpectedTextLevelToTextLevelDestination > 0) {
    lines.push(
      `omittedExpectedTextLevelToTextLevelDestination=${summary.omittedExpectedTextLevelToTextLevelDestination} (text_level_to_text_level: destination has block id only; no dest match/chunk — not an injection gap)`
    );
  }
  lines.push("");
  lines.push(
    `--- Skipped autogen (${summary.skippedAutogen.length}) — fix injection / matching logic ---`
  );
  for (const e of summary.skippedAutogen) {
    lines.push(
      `  ${e.linkId} | ref_type=${e.refType} | ${e.skipReason}${e.detail ? ` | ${e.detail}` : ""}`
    );
    lines.push(
      `    htmlId=${e.primaryHtmlId ?? "(none)"} | match=${JSON.stringify(e.uniqueMatchText ?? "")} | chunk=${JSON.stringify(e.uniqueChunkText ?? "")}`
    );
  }
  return lines.join("\n");
}

export function logDocumentLinkInjectionDevReport(options: {
  documentId: string | undefined;
  report: DocumentLinkInjectionDebugReport;
  documentLinksMap: Map<string, DocumentLink>;
  /** When true, also logs {@link formatDocumentLinkInjectionReport} (verbose). Full data is always on `window.__LARA_DOC_LINK_INJECTION_DEBUG__.report`. */
  includeFullPlanBuilderReport?: boolean;
  /**
   * Matches `DocumentReferencesTab` filter defaults (`showHistorical` on,
   * `showEarlierWorkspaceVersions` off — workspace rows collapsed to latest revision).
   */
  referencesSidebarToggles?: {
    showHistorical?: boolean;
    showEarlierWorkspaceVersions?: boolean;
  };
}): AutogenInjectionDebugSummary {
  const {
    documentId,
    report,
    documentLinksMap,
    includeFullPlanBuilderReport = false,
    referencesSidebarToggles,
  } = options;

  const summary = summarizeAutogenDocumentLinkInjection(
    report,
    documentLinksMap,
    documentId
  );

  const autogenText = formatAutogenSkippedInjectionReport(summary);

  let referencesSidebarSummary: ReferencesSidebarVisibilitySummary | undefined;
  if (documentId) {
    const byId = new Map<string, DocumentLink>();
    for (const link of documentLinksMap.values()) {
      if (!byId.has(link.id)) byId.set(link.id, link);
    }
    const allLinks = Array.from(byId.values());
    referencesSidebarSummary = summarizeReferencesSidebarVisibility(
      allLinks,
      documentId,
      {
        showHistorical: referencesSidebarToggles?.showHistorical,
        showEarlierWorkspaceVersions:
          referencesSidebarToggles?.showEarlierWorkspaceVersions,
      }
    );
  }

  console.groupCollapsed(
    `[Lara] Document link injection — ${documentId ?? "?"}` +
      ` | autogen skipped (actionable): ${summary.skippedAutogen.length}` +
      (summary.omittedExpectedDocumentLevelNoAnchor > 0
        ? ` | omitted (document-level side): ${summary.omittedExpectedDocumentLevelNoAnchor}`
        : "") +
      (summary.omittedExpectedTextLevelToTextLevelDestination > 0
        ? ` | omitted (tl2tl dest): ${summary.omittedExpectedTextLevelToTextLevelDestination}`
        : "") +
      ` / injected autogen: ${summary.injectedAutogenCount}` +
      ` / total: ${summary.totalLinksInPlan}` +
      (referencesSidebarSummary
        ? ` | sidebar shown: ${referencesSidebarSummary.shownInSidebarCount}/${referencesSidebarSummary.totalFetched}`
        : "")
  );
  console.log(autogenText);
  if (referencesSidebarSummary) {
    console.log(formatReferencesSidebarVisibilityReport(referencesSidebarSummary));
  }
  if (includeFullPlanBuilderReport) {
    console.log(formatDocumentLinkInjectionReport(report));
  }
  console.groupEnd();

  if (typeof window !== "undefined" && import.meta.env.DEV) {
    (
      window as unknown as {
        __LARA_DOC_LINK_INJECTION_DEBUG__?: {
          documentId?: string;
          at: number;
          summary: AutogenInjectionDebugSummary;
          report: DocumentLinkInjectionDebugReport;
          referencesSidebarSummary?: ReferencesSidebarVisibilitySummary;
        };
      }
    ).__LARA_DOC_LINK_INJECTION_DEBUG__ = {
      documentId,
      at: Date.now(),
      summary,
      report,
      ...(referencesSidebarSummary ? { referencesSidebarSummary } : {}),
    };
  }

  return summary;
}
